/**
 * ExecutionPreview.tsx
 * 
 * Read-only preview of execution-level assignments before campaign creation.
 * Shows the resolved store × format × operator assignments with quantities.
 * 
 * IMPORTANT: This is a preview, not an editor. No inline editing allowed.
 * Uses the same resolution logic as snapshot generation.
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
    AlertCircle,
    AlertTriangle,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    Search,
    Store as StoreIcon,
    User,
    Image as FormatIcon,
    Loader2,
    X,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import type { StoreFormatGroup } from '@shared/store-format-groups';

// ============================================================================
// Types
// ============================================================================

interface ExecutionAssignment {
    storeId: number;
    storeName: string;
    storeAddress: string | null;
    storeNumber: string | null;
    mediaFormatId: number;
    mediaFormatName: string;
    quantity: number;
    operatorId: number | null;
    operatorName: string | null;
    resolutionStatus: 'resolved' | 'warning' | 'missing';
    resolutionMessage: string | null;
}

interface ExecutionPreviewResponse {
    success: boolean;
    assignments: ExecutionAssignment[];
    totals: {
        storeCount: number;
        assignmentCount: number;
        totalUnits: number;
        resolvedCount: number;
        warningCount: number;
        missingCount: number;
    };
    errors: Array<{ type: string; message: string; storeId?: number; mediaFormatId?: number }>;
    warnings: Array<{ type: string; message: string; storeId?: number; mediaFormatId?: number }>;
}

interface StoreGroup {
    storeId: number;
    storeName: string;
    storeAddress: string | null;
    storeNumber: string | null;
    assignments: ExecutionAssignment[];
    hasWarnings: boolean;
    hasMissing: boolean;
    totalUnits: number;
}

interface ExecutionPreviewProps {
    groups: StoreFormatGroup[];
    onBack?: () => void;
    onContinue?: () => void;
    isSubmitting?: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

function groupByStore(assignments: ExecutionAssignment[]): StoreGroup[] {
    const storeMap = new Map<number, StoreGroup>();

    for (const assignment of assignments) {
        let group = storeMap.get(assignment.storeId);
        if (!group) {
            group = {
                storeId: assignment.storeId,
                storeName: assignment.storeName,
                storeAddress: assignment.storeAddress,
                storeNumber: assignment.storeNumber,
                assignments: [],
                hasWarnings: false,
                hasMissing: false,
                totalUnits: 0,
            };
            storeMap.set(assignment.storeId, group);
        }
        group.assignments.push(assignment);
        group.totalUnits += assignment.quantity;
        if (assignment.resolutionStatus === 'warning') {
            group.hasWarnings = true;
        }
        if (assignment.resolutionStatus === 'missing') {
            group.hasMissing = true;
        }
    }

    // Sort by store name
    return Array.from(storeMap.values()).sort((a, b) => 
        a.storeName.localeCompare(b.storeName)
    );
}

// ============================================================================
// Sub-components
// ============================================================================

interface StoreRowProps {
    store: StoreGroup;
    isExpanded: boolean;
    onToggle: () => void;
}

function StoreRow({ store, isExpanded, onToggle }: StoreRowProps) {
    const operatorCount = new Set(
        store.assignments.filter(a => a.operatorId).map(a => a.operatorId)
    ).size;

    return (
        <Collapsible open={isExpanded} onOpenChange={onToggle}>
            <CollapsibleTrigger asChild>
                <div className={`
                    flex items-center justify-between p-3 cursor-pointer select-none
                    hover:bg-muted/50 transition-colors border-b
                    ${store.hasMissing ? 'bg-red-50/50' : store.hasWarnings ? 'bg-yellow-50/50' : ''}
                `}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                        {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                        ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                        )}
                        
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="font-medium truncate">{store.storeName}</span>
                                {store.storeNumber && (
                                    <Badge variant="outline" className="text-xs shrink-0">
                                        #{store.storeNumber}
                                    </Badge>
                                )}
                            </div>
                            {store.storeAddress && (
                                <p className="text-xs text-muted-foreground truncate">
                                    {store.storeAddress}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <FormatIcon className="h-3 w-3" />
                            <span>{store.assignments.length}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <User className="h-3 w-3" />
                            <span>{operatorCount}</span>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                            {store.totalUnits} units
                        </Badge>
                        
                        {store.hasMissing && (
                            <Badge variant="destructive" className="text-xs">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                Missing
                            </Badge>
                        )}
                        {store.hasWarnings && !store.hasMissing && (
                            <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Warning
                            </Badge>
                        )}
                        {!store.hasMissing && !store.hasWarnings && (
                            <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Ready
                            </Badge>
                        )}
                    </div>
                </div>
            </CollapsibleTrigger>

            <CollapsibleContent>
                <div className="bg-muted/20 border-b">
                    <Table>
                        <TableHeader>
                            <TableRow className="text-xs">
                                <TableHead className="w-[200px]">Format</TableHead>
                                <TableHead className="w-[150px]">Operator</TableHead>
                                <TableHead className="w-[80px] text-right">Qty</TableHead>
                                <TableHead className="w-[100px]">Status</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {store.assignments.map((assignment, idx) => (
                                <TableRow key={`${assignment.storeId}-${assignment.mediaFormatId}-${idx}`} className="text-sm">
                                    <TableCell className="py-2">
                                        {assignment.mediaFormatName}
                                    </TableCell>
                                    <TableCell className="py-2">
                                        {assignment.operatorName ? (
                                            <span className="flex items-center gap-1">
                                                <User className="h-3 w-3 text-muted-foreground" />
                                                {assignment.operatorName}
                                            </span>
                                        ) : (
                                            <span className="text-red-600 flex items-center gap-1">
                                                <AlertCircle className="h-3 w-3" />
                                                Not assigned
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell className="py-2 text-right font-medium">
                                        {assignment.quantity}
                                    </TableCell>
                                    <TableCell className="py-2">
                                        {assignment.resolutionStatus === 'resolved' && (
                                            <span className="text-green-600 flex items-center gap-1 text-xs">
                                                <CheckCircle2 className="h-3 w-3" />
                                                Resolved
                                            </span>
                                        )}
                                        {assignment.resolutionStatus === 'warning' && (
                                            <span className="text-yellow-600 flex items-center gap-1 text-xs">
                                                <AlertTriangle className="h-3 w-3" />
                                                Warning
                                            </span>
                                        )}
                                        {assignment.resolutionStatus === 'missing' && (
                                            <span className="text-red-600 flex items-center gap-1 text-xs">
                                                <AlertCircle className="h-3 w-3" />
                                                Missing
                                            </span>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}

// ============================================================================
// Main Component
// ============================================================================

export function ExecutionPreview({
    groups,
    onBack,
    onContinue,
    isSubmitting = false,
}: ExecutionPreviewProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedStores, setExpandedStores] = useState<Set<number>>(new Set());

    // Fetch execution preview data
    const { data, isLoading, error } = useQuery<ExecutionPreviewResponse>({
        queryKey: ['/api/campaigns/0/groups/execution-preview', groups],
        queryFn: async () => {
            const response = await apiFetch('/api/campaigns/0/groups/execution-preview', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ groups }),
            });
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to generate execution preview: ${response.status} - ${errorText}`);
            }
            return response.json();
        },
        enabled: groups.length > 0,
        staleTime: 0, // Always refetch when groups change
        retry: 1, // Only retry once
    });

    // Group assignments by store
    const storeGroups = useMemo(() => {
        if (!data?.assignments) return [];
        return groupByStore(data.assignments);
    }, [data?.assignments]);

    // Filter stores by search
    const filteredStoreGroups = useMemo(() => {
        if (!searchQuery.trim()) return storeGroups;
        const query = searchQuery.toLowerCase();
        return storeGroups.filter(store =>
            store.storeName.toLowerCase().includes(query) ||
            store.storeAddress?.toLowerCase().includes(query) ||
            store.storeNumber?.toLowerCase().includes(query)
        );
    }, [storeGroups, searchQuery]);

    // Toggle store expansion
    const toggleStore = (storeId: number) => {
        setExpandedStores(prev => {
            const next = new Set(prev);
            if (next.has(storeId)) {
                next.delete(storeId);
            } else {
                next.add(storeId);
            }
            return next;
        });
    };

    // Expand/collapse all
    const expandAll = () => {
        setExpandedStores(new Set(filteredStoreGroups.map(s => s.storeId)));
    };

    const collapseAll = () => {
        setExpandedStores(new Set());
    };

    // Check if there are blocking errors
    const hasBlockingErrors = (data?.errors?.length ?? 0) > 0;
    const hasWarnings = (data?.warnings?.length ?? 0) > 0;

    if (groups.length === 0) {
        return (
            <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                    <StoreIcon className="h-12 w-12 text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground text-center">
                        No groups configured. Go back to add groups.
                    </p>
                    {onBack && (
                        <Button variant="outline" onClick={onBack} className="mt-4">
                            Back to Groups
                        </Button>
                    )}
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                        Execution Preview
                        <Badge variant="outline" className="text-xs font-normal">
                            Read-only
                        </Badge>
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                        Review the exact assignments that will be created. This shows who is doing what at each store.
                    </p>
                </CardHeader>
            </Card>

            {/* Totals Summary */}
            {data?.totals && (
                <Card>
                    <CardContent className="pt-4">
                        <div className="grid grid-cols-2 sm:grid-cols-6 gap-4 text-sm">
                            <div>
                                <p className="text-muted-foreground">Stores</p>
                                <p className="text-2xl font-bold">{data.totals.storeCount}</p>
                            </div>
                            <div>
                                <p className="text-muted-foreground">Assignments</p>
                                <p className="text-2xl font-bold">{data.totals.assignmentCount}</p>
                            </div>
                            <div>
                                <p className="text-muted-foreground">Total Units</p>
                                <p className="text-2xl font-bold">{data.totals.totalUnits}</p>
                            </div>
                            <div>
                                <p className="text-muted-foreground">Resolved</p>
                                <p className="text-2xl font-bold text-green-600">{data.totals.resolvedCount}</p>
                            </div>
                            <div>
                                <p className="text-muted-foreground">Warnings</p>
                                <p className={`text-2xl font-bold ${data.totals.warningCount > 0 ? 'text-yellow-600' : ''}`}>
                                    {data.totals.warningCount}
                                </p>
                            </div>
                            <div>
                                <p className="text-muted-foreground">Missing</p>
                                <p className={`text-2xl font-bold ${data.totals.missingCount > 0 ? 'text-red-600' : ''}`}>
                                    {data.totals.missingCount}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Warnings/Errors Banner */}
            {hasWarnings && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
                    <div className="text-sm text-yellow-800">
                        <strong>{data?.totals?.missingCount || 0} assignment(s) missing operators.</strong>{' '}
                        You can still create the campaign, but these will need to be assigned later.
                    </div>
                </div>
            )}

            {hasBlockingErrors && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                    <div className="text-sm text-red-800">
                        <strong>There are validation errors that must be fixed.</strong>
                        <ul className="list-disc list-inside mt-1">
                            {data?.errors?.slice(0, 5).map((err, i) => (
                                <li key={i}>{err.message}</li>
                            ))}
                            {(data?.errors?.length ?? 0) > 5 && (
                                <li>...and {(data?.errors?.length ?? 0) - 5} more</li>
                            )}
                        </ul>
                    </div>
                </div>
            )}

            {/* Store List */}
            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-base">
                            Store Assignments ({filteredStoreGroups.length} stores)
                        </CardTitle>
                        <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={expandAll}>
                                Expand All
                            </Button>
                            <Button variant="ghost" size="sm" onClick={collapseAll}>
                                Collapse All
                            </Button>
                        </div>
                    </div>
                    
                    {/* Search */}
                    <div className="relative mt-2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            type="text"
                            placeholder="Search stores..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 pr-9"
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </CardHeader>

                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            <span className="ml-2 text-muted-foreground">Loading execution preview...</span>
                        </div>
                    ) : error ? (
                        <div className="flex items-center justify-center py-12 text-red-600">
                            <AlertCircle className="h-5 w-5 mr-2" />
                            <span>Failed to load preview. Please try again.</span>
                        </div>
                    ) : filteredStoreGroups.length === 0 ? (
                        <div className="flex items-center justify-center py-12 text-muted-foreground">
                            {searchQuery ? `No stores match "${searchQuery}"` : 'No assignments to display'}
                        </div>
                    ) : (
                        <div className="border-t">
                            {filteredStoreGroups.map(store => (
                                <StoreRow
                                    key={store.storeId}
                                    store={store}
                                    isExpanded={expandedStores.has(store.storeId)}
                                    onToggle={() => toggleStore(store.storeId)}
                                />
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                    {data?.totals && (
                        <>
                            {data.totals.resolvedCount} of {data.totals.assignmentCount} assignments fully resolved
                        </>
                    )}
                </div>
                <div className="flex gap-2">
                    {onBack && (
                        <Button variant="outline" onClick={onBack} disabled={isSubmitting}>
                            Back to Groups
                        </Button>
                    )}
                    {onContinue && (
                        <Button 
                            onClick={onContinue} 
                            disabled={isSubmitting || hasBlockingErrors}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Processing...
                                </>
                            ) : (
                                'Continue to Details'
                            )}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ExecutionPreview;
